import { createServerFn } from "@tanstack/react-start";

const CATEGORIES = [
  "infrastructure", "environment", "security", "healthcare", "education",
  "transportation", "water", "electricity", "waste", "governance",
  "emergency", "community",
] as const;

const POST_TYPES = [
  "update", "issue", "question", "discussion",
  "announcement", "emergency", "success", "project",
] as const;

type Classification = {
  category: (typeof CATEGORIES)[number];
  post_type: (typeof POST_TYPES)[number];
  severity: "info" | "warning" | "critical";
  tags: string[];
  title: string;
};

export const classifyPost = createServerFn({ method: "POST" })
  .inputValidator((data: { content: string; locationName?: string | null }) => data)
  .handler(async ({ data }): Promise<Classification> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    const fallback: Classification = {
      category: "community",
      post_type: "update",
      severity: "info",
      tags: [],
      title: data.content.trim().split(/\s+/).slice(0, 12).join(" ").slice(0, 120) || "Update",
    };
    if (!apiKey) return fallback;

    const sys = `You classify short civic social posts. Return strict JSON only, no prose.
Schema:
{
  "category": one of ${CATEGORIES.join(", ")},
  "post_type": one of ${POST_TYPES.join(", ")},
  "severity": one of info, warning, critical (critical only for true emergencies),
  "tags": 1-5 lowercase, hyphenated topical tags,
  "title": a concise headline under 80 chars derived from the post
}
Rules: questions => post_type "question". Urgent danger (fire, flooding, road blocked, outage right now) => post_type "emergency", category "emergency", severity "warning" or "critical". Power outage => "electricity". Water issue => "water". Project/event proposals => "project" or "announcement".`;

    const user = `Post: """${data.content.slice(0, 1200)}"""\nLocation: ${data.locationName || "unknown"}`;

    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash-lite",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          response_format: { type: "json_object" },
        }),
      });
      if (!res.ok) return fallback;
      const json = await res.json();
      const raw = json?.choices?.[0]?.message?.content;
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      const category = CATEGORIES.includes(parsed.category) ? parsed.category : fallback.category;
      const post_type = POST_TYPES.includes(parsed.post_type) ? parsed.post_type : fallback.post_type;
      const severity = ["info", "warning", "critical"].includes(parsed.severity) ? parsed.severity : "info";
      const tags = Array.isArray(parsed.tags)
        ? parsed.tags
            .map((t: any) => String(t).toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+|-+$/g, ""))
            .filter(Boolean)
            .slice(0, 5)
        : [];
      const title = typeof parsed.title === "string" && parsed.title.trim()
        ? parsed.title.trim().slice(0, 140)
        : fallback.title;
      return { category, post_type, severity, tags, title };
    } catch {
      return fallback;
    }
  });
